/**
 * Fallback business description for the final onboarding submit.
 *
 * `businessDescription` is optional in the form, but bottimmo's US material
 * generation preflight rejects `submitOnboarding` when
 * `CompanyMeta.businessDescription` is empty. On submit (never on autosave, so
 * the default never shows up in the textarea) we persist a description built
 * from the company name and address instead.
 */
type DescriptionSource = {
  name?: string | null
  street?: string | null
  zipcode?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
}

type CompanyWithMeta = { meta?: { businessDescription?: string | null } | null } | null | undefined

const trimmed = (value: unknown) => (value == null ? '' : String(value).trim())

/** "Name, Street, Zip, City, State, Country" from the non-empty parts, or `''`. */
export function buildDefaultBusinessDescription(formData: DescriptionSource): string {
  return [
    formData.name,
    formData.street,
    formData.zipcode,
    formData.city,
    formData.state,
    formData.country,
  ]
    .map(trimmed)
    .filter(Boolean)
    .join(', ')
}

/**
 * Description to persist on submit, or `null` when no meta write is needed:
 *  - the user's trimmed description when they entered one,
 *  - `null` when the company already has a stored description (kept as is),
 *  - otherwise the default from `buildDefaultBusinessDescription` (or `null`
 *    when that is empty too).
 */
export function resolveSubmitBusinessDescription(
  formData: DescriptionSource & { businessDescription?: string | null },
  company: CompanyWithMeta
): string | null {
  const description = trimmed(formData.businessDescription)
  if (description) return description
  if (trimmed(company?.meta?.businessDescription)) return null
  return buildDefaultBusinessDescription(formData) || null
}
